<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import completeImage from 'dashboard/assets/real-estate/post-sale/concluir-processo-1.png';
import recentImage from 'dashboard/assets/real-estate/nucleo-work-order-request/concluir-processo-2.png';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  postSaleStatus: { type: String, default: '' },
  transaction: { type: Object, default: null },
});

const emit = defineEmits(['close', 'confirm']);
const { t } = useI18n();
const completedPostSaleStatuses = new Set([
  'registro',
  'entrega_chaves',
  'finalizado',
]);

const isCompleteFlow = computed(() =>
  completedPostSaleStatuses.has(
    props.postSaleStatus || props.transaction?.post_sale?.status
  )
);
const copy = computed(() => {
  if (isCompleteFlow.value) {
    return {
      title: t('SALES.FINALIZE.COMPLETE.TITLE'),
      image: completeImage,
      paragraphs: [
        {
          value: t('SALES.FINALIZE.COMPLETE.LEAD'),
          nextLine: t('SALES.FINALIZE.COMPLETE.LEAD_COMPLETION'),
          strong: true,
        },
        { value: t('SALES.FINALIZE.COMPLETE.DESCRIPTION') },
        { value: t('SALES.FINALIZE.COMPLETE.REMINDER') },
      ],
    };
  }

  return {
    title: t('SALES.FINALIZE.RECENT.TITLE'),
    image: recentImage,
    paragraphs: [
      { value: t('SALES.FINALIZE.RECENT.LEAD') },
      { value: t('SALES.FINALIZE.RECENT.REMINDER'), strong: true },
      {
        label: t('SALES.FINALIZE.RECENT.NOTE_LABEL'),
        value: t('SALES.FINALIZE.RECENT.NOTE'),
      },
    ],
  };
});
</script>

<template>
  <LaisModal
    :show="show"
    max-width-class="max-w-[1040px]"
    header-class="absolute right-2 top-2 z-10 !border-0 !p-0"
    @close="emit('close')"
  >
    <template #header>
      <span class="sr-only">{{ copy.title }}</span>
    </template>
    <div
      class="grid overflow-hidden rounded-lais-lg md:grid-cols-[minmax(0,482px)_minmax(0,1fr)]"
    >
      <img
        data-testid="sales-finalize-dialog-image"
        :src="copy.image"
        alt=""
        aria-hidden="true"
        class="h-auto w-full object-cover"
      />
      <div class="grid content-center gap-0 p-5 sm:p-7">
        <h2
          class="max-w-[385px] font-redHatDisplay text-xl font-bold leading-7 text-lais-text"
        >
          {{ copy.title }}
        </h2>
        <p
          v-for="(paragraph, index) in copy.paragraphs"
          :key="index"
          class="mt-[25px] text-sm leading-6 text-lais-text-body"
          :class="paragraph.strong ? 'font-bold text-lais-text' : ''"
        >
          <span v-if="paragraph.label" class="font-bold text-lais-text">
            {{ paragraph.label }}
          </span>
          {{ paragraph.value }}
          <span v-if="paragraph.nextLine" class="block">
            {{ paragraph.nextLine }}
          </span>
        </p>
      </div>
    </div>
    <template #footer>
      <div class="flex justify-end gap-2">
        <LaisPrimaryButton variant="ghost" @click="emit('close')">
          {{ t('SALES.FINALIZE.BACK') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          icon="i-lucide-check"
          :is-loading="isLoading"
          @click="emit('confirm')"
        >
          {{ t('SALES.FINALIZE.CONFIRM') }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>
</template>
